/**
 * Theme registry + localStorage-backed apply/get/set helpers. The CSS
 * token definitions live in `src/index.css` — this module just toggles
 * the matching body class.
 *
 * Adding a new theme:
 *   1. Define `.theme-<name>` in `src/index.css` overriding the
 *      `--color-*` semantic tokens.
 *   2. Add it to `THEMES` below.
 *   Components don't change — they reference Tailwind utilities
 *   (`bg-primary`, `text-foreground`, …) that resolve through the tokens.
 */

export type ThemeId = 'default' | 'blue' | 'dark' | 'high-contrast';

export type ThemeDescriptor = {
  id: ThemeId;
  label: string;
  /** Short blurb shown next to the picker for context. */
  description: string;
  /** Used by the swatch preview — pulled from the theme's primary color. */
  previewColor: string;
};

export const THEMES: ThemeDescriptor[] = [
  {
    id: 'default',
    label: 'RxFood (default)',
    description: 'Brand teal with warm neutrals — the nutrition-first default.',
    previewColor: '#36C2B4',
  },
  {
    id: 'blue',
    label: 'Clinical blue',
    description: 'Cooler, enterprise-safe palette.',
    previewColor: '#2D9CDB',
  },
  {
    id: 'dark',
    label: 'Dark',
    description: 'Charcoal surfaces with the green accent.',
    previewColor: '#121212',
  },
  {
    id: 'high-contrast',
    label: 'High contrast',
    description: 'Maximum contrast for accessibility.',
    previewColor: '#000000',
  },
];

const STORAGE_KEY = 'ui-theme';
const DEFAULT_THEME: ThemeId = 'default';
const THEME_CLASS_PREFIX = 'theme-';
const ALL_THEME_CLASSES = THEMES.map((t) => `${THEME_CLASS_PREFIX}${t.id}`);

const isValidTheme = (value: unknown): value is ThemeId =>
  typeof value === 'string' && THEMES.some((t) => t.id === value);

/** Read the persisted theme id (or the default if none/invalid). */
export const getStoredTheme = (): ThemeId => {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isValidTheme(raw) ? raw : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
};

/**
 * Apply a theme. Sets THREE things so the CSS catches it no matter how
 * the selectors are written:
 *
 *   1. `data-theme` attribute on `<html>` — primary selector hook.
 *      Using `<html>` (not `<body>`) means `:root[data-theme="X"]`
 *      works alongside the default `:root` rule without specificity
 *      surprises, and avoids any "another stylesheet resets body
 *      class" gotcha.
 *   2. `theme-<id>` class on `<body>` — backward-compat with any
 *      consumer that's already keyed on the class form.
 *   3. `.dark` on `<body>` for the dark theme — keeps Tailwind's
 *      built-in `dark:` variants firing.
 */
export const applyTheme = (id: ThemeId): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', id);
  const body = document.body;
  if (body) {
    for (const cls of ALL_THEME_CLASSES) body.classList.remove(cls);
    body.classList.add(`${THEME_CLASS_PREFIX}${id}`);
    body.classList.toggle('dark', id === 'dark');
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* localStorage may be unavailable (private mode); theme still applied in-memory. */
  }
};

/** One-shot bootstrap — call before React renders so the first paint is themed. */
export const initTheme = (): void => {
  applyTheme(getStoredTheme());
};
